import Stack from '@mui/material/Stack'
import { Box, useTheme } from '@mui/material'
import {
  ArrowBackIosOutlined,
  Bookmark,
  CalendarTodayOutlined,
  ChevronLeftOutlined,
  DescriptionOutlined,
  MedicalServicesOutlined,
  PlaceOutlined,
  ShareOutlined,
} from '@mui/icons-material'
import { useGetPublicJobAdDetailQuery } from '@front/api/service-career-neo/useGetPublicJobAdDetailQuery'
import { useGetPublicCareerSiteQuery } from '@front/api/service-career-neo/useGetPublicCareerSiteQuery'
import { extractCity } from '../../utils/extractCity'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import {
  ExtraSmallText,
  Heading2,
  TextBody,
  TextBodyBold,
  type TypographyColor,
} from '@front/components/data-display/typography/Typography'
import { capitalizeFirst } from '@hublo/utils/string'
import { VanillaButtonStyle } from '@front/shared/constants/styles/VanillaButtonStyle'
import { InstitutionLogo } from '../shared/institution-logo/InstitutionLogo'
import { DisplayRichText } from '@front/components/inputs/rich-text/DisplayRichText'
import type { ParseKeys } from 'i18next'
import { getRuntimeConfig } from '@front/env/config/career'
import EmptyPage from '../sections/empty-page/EmptyPage'
import CareerTag from '../shared/career-tag/CareerTag'
import InstitutionCard from '../shared/institution-card/InstitutionCard'
import ApplyCard from '../shared/apply-card/ApplyCard'
import { TopBar } from '@front/components/navigation/top-bar/TopBar'
import { BottomActionBar } from '@front/components/surfaces'
import { useNavigate } from '@tanstack/react-router'
import { JOB_OFFER_CONTEXT } from '@hublo/enums/recruitment/job-offer-context.enum'
import { buildJobApplyRoute } from '../../utils/buildJobApplyRoute'

type PageProps = {
  slug: string
  uuid: string
  referer?: string
}

export default function JobAdPage({ slug, uuid, referer }: PageProps) {
  const monorepoAppUrl = getRuntimeConfig().public.monorepoBaseUrl

  const navigate = useNavigate()
  const theme = useTheme()

  const { t } = useTranslation([
    'main.common',
    'main.worker',
    'main.talent',
    'network.career_website',
  ])
  const tagStyle = {
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
  }
  const { t: tJobs } = useTranslation('common.jobs')

  const {
    data: institutionData,
    isLoading: institutionIsLoading,
    isError: institutionIsError,
  } = useGetPublicCareerSiteQuery(slug as string)

  const {
    data: offerData,
    isLoading: offerIsLoading,
    isError: offerIsError,
  } = useGetPublicJobAdDetailQuery(uuid as string)

  // Google Maps
  const mapsAddress = institutionData?.address?.replaceAll(' ', '+') ?? ''
  const mapsAddressUrl = `https://www.google.fr/maps/place/${mapsAddress}`

  const goToMaps = () => {
    window.open(mapsAddressUrl, '_blank', 'noopener, noreferrer')
  }

  // Handlers
  const goToCareerSite = async () => {
    await navigate({ to: `/${slug}` })
  }

  const goToJobApply = () => {
    /**@TODO */
    /**
     * Implementation of a more robust way to switch between Career and Caregivers.
     * ⚠️ Be wary of any path changes on the worker app.
     */
    const route = buildJobApplyRoute(offerData, referer)

    window.open(`${monorepoAppUrl}${route}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      {institutionIsError || offerIsError ? (
        <EmptyPage mode="ad" />
      ) : (
        <>
          {/**@MobileActions */}
          <Stack
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              bottom: 0,
              width: '100%',
            }}
          >
            <BottomActionBar
              primaryButtonProps={{
                'data-testid': 'JobArea__JobDetails__btn_main_action',
                loading: offerIsLoading,
                label: t('main.worker:shift_details.button_apply'),
                onClick: goToJobApply,
                variant: 'primary',
              }}
            />
          </Stack>
          <Stack
            sx={{ display: { xs: 'flex', md: 'none' }, position: 'relative' }}
          >
            <TopBar
              leftButton={{
                icon: <ArrowBackIosOutlined />,
                onClick: goToCareerSite,
                'data-testid': 'JobOffers__JobOfferDetails__btn_go_back',
              }}
              title={t('main.common:back')}
            />
          </Stack>

          {/**@MainContainer */}
          <Stack
            sx={{
              width: '100%',
              minHeight: '100vh',
              backgroundColor: theme.palette.background.surface,
              alignItems: 'center',
            }}
          >
            {/**@InfoHeader */}
            <Stack
              sx={{
                width: '100%',
                maxWidth: '1168px',
                gap: 2,
                my: 3,
                px: 2,
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {/**@Note - This is a requested custom button */}
                <button
                  style={{
                    ...VanillaButtonStyle,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingTop: 8,
                    paddingBottom: 8,
                  }}
                  onClick={goToCareerSite}
                >
                  <ChevronLeftOutlined
                    sx={{
                      width: 24,
                      height: 24,
                      color: theme.palette.text.brand,
                    }}
                  />
                  <TextBodyBold sx={{ color: theme.palette.text.brand }}>
                    {t('main.common:back')}
                  </TextBodyBold>
                </button>
              </Box>

              {/**@Logo */}
              <Stack
                sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}
              >
                {institutionIsLoading && !institutionData && (
                  <div className="flex items-center flex-row gap-2 opacity-10">
                    <div className="w-10 h-10 rounded-sm animate-pulse bg-black" />
                    <div className="w-50 h-5 rounded-full animate-pulse bg-black" />
                  </div>
                )}

                {institutionData && (
                  <>
                    <InstitutionLogo
                      url={institutionData?.logoUrl as string}
                      alt={institutionData?.name as string}
                    />
                    <TextBodyBold>{institutionData?.name}</TextBodyBold>
                  </>
                )}
              </Stack>

              {/**@JobName (and buttons) */}
              <Stack
                sx={{ gap: 2, flexDirection: 'row', alignItems: 'center ' }}
              >
                <Stack sx={{ flex: 1 }}>
                  {offerIsLoading && !offerData && (
                    <div className="opacity-10">
                      <div className="w-50 h-7.5 rounded-full animate-pulse bg-black" />
                    </div>
                  )}

                  {offerData && (
                    <Heading2 sx={{ flex: 1 }}>{offerData?.title}</Heading2>
                  )}
                </Stack>
                <button
                  style={{
                    ...VanillaButtonStyle,
                    width: 24,
                    height: 24,
                  }}
                >
                  <Bookmark sx={{ color: theme.palette.text.brand }} />
                </button>
                <button
                  style={{
                    ...VanillaButtonStyle,
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                  }}
                >
                  <ShareOutlined sx={{ color: theme.palette.text.brand }} />
                </button>
              </Stack>

              {/**@ChipStack */}
              {(institutionIsLoading || offerIsLoading) &&
                (!institutionData || !offerData) && (
                  <div className="flex items-center flex-row gap-4 opacity-10">
                    <div className="w-50 h-6 rounded-full animate-pulse bg-black" />
                    <div className="w-50 h-6 rounded-full animate-pulse bg-black" />
                  </div>
                )}

              {institutionData && offerData && (
                <Stack sx={{ gap: 2, flexDirection: 'row', flexWrap: 'wrap' }}>
                  {/**@ContractType */}
                  {offerData?.contractTypes?.[0]?.name && (
                    <CareerTag
                      label={offerData?.contractTypes?.[0]?.name as string}
                      icon={<DescriptionOutlined sx={tagStyle} />}
                    />
                  )}

                  {offerData?.context === JOB_OFFER_CONTEXT.REPLACEMENT && (
                    <CareerTag
                      label={t('main.worker:job_offers.filter.vacations')}
                      icon={<DescriptionOutlined sx={tagStyle} />}
                    />
                  )}

                  {/* City */}
                  {institutionData?.address && (
                    <CareerTag
                      label={
                        extractCity(String(institutionData?.address)) as string
                      }
                      icon={<PlaceOutlined sx={tagStyle} />}
                    />
                  )}

                  {/* Job positions */}
                  {(offerData?.jobs ?? []).length > 0 &&
                    offerData?.jobs?.map((j) => (
                      <CareerTag
                        label={tJobs(j.slice(12) as ParseKeys<'common.jobs'>)}
                        icon={<MedicalServicesOutlined sx={tagStyle} />}
                        key={j}
                      />
                    ))}

                  {/* Start date */}
                  {offerData.context !== JOB_OFFER_CONTEXT.REPLACEMENT && (
                    <CareerTag
                      label={
                        offerData?.isAsap || !offerData?.contractStartDate
                          ? t('main.worker:job_offers.as_soon_as_possible')
                          : DateTime.fromISO(
                              offerData?.contractStartDate,
                            ).toLocaleString(DateTime.DATE_SHORT)
                      }
                      icon={<CalendarTodayOutlined sx={tagStyle} />}
                    />
                  )}
                </Stack>
              )}

              {/* Creation Date */}
              {offerIsLoading && !offerData && (
                <div className="flex items-center flex-row gap-4 opacity-10">
                  <div className="w-25 h-4.5 rounded-full animate-pulse bg-black" />
                </div>
              )}

              {Boolean(offerData?.publishedAt) && (
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexDirection: 'row',
                  }}
                >
                  <CalendarTodayOutlined
                    sx={{
                      width: '12px',
                      height: '13.333px',
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <ExtraSmallText
                    color={theme.palette.text.helper as TypographyColor}
                    data-testid="JobOffers__JobOfferCard__publishedAt"
                  >
                    {capitalizeFirst(
                      DateTime.fromISO(
                        offerData?.publishedAt ?? '',
                      ).toRelativeCalendar() as string,
                    )}
                  </ExtraSmallText>
                </Stack>
              )}
            </Stack>

            <Box
              sx={{
                width: '100%',
                height: '1px',
                backgroundColor: theme.palette.border['brand-subtle'],
              }}
            />

            <Stack
              sx={{
                width: '100%',
                maxWidth: '1168px',
                flexDirection: 'row',
                gap: 8,
                my: { xs: 4, md: 5 },
                px: 2,
              }}
            >
              {/**@LeftSection - Description */}
              <Stack sx={{ flex: 1, gap: 4 }}>
                {(institutionIsLoading || offerIsLoading) &&
                  (!institutionData || !offerData) && (
                    <div className="flex flex-col gap-4 opacity-10">
                      <div className="w-75 h-6 rounded-full animate-pulse bg-black" />
                      <div className="w-50 h-6 rounded-full animate-pulse bg-black" />
                      <div className="w-25 h-6 rounded-full animate-pulse bg-black" />
                    </div>
                  )}

                {institutionData && offerData && (
                  <>
                    <Stack sx={{ gap: 2 }}>
                      <Heading2>
                        {t('main.worker:job_offers.result_item.title')}
                      </Heading2>
                      <DisplayRichText value={offerData?.description ?? ''} />
                    </Stack>
                    <Box
                      sx={{
                        width: '100%',
                        height: '1px',
                        backgroundColor: theme.palette.border['brand-subtle'],
                      }}
                    />

                    {/**@InstitutionCard */}
                    <InstitutionCard
                      slug={slug}
                      logoUri={institutionData?.logoUrl as string}
                      name={institutionData?.name as string}
                    />

                    {/**@InstitutionInfo */}
                    <Stack
                      sx={{
                        gap: 2,
                      }}
                    >
                      <Heading2>
                        {t('main.talent:job_offer.details.institution')}
                      </Heading2>
                      {institutionData?.address && (
                        <button style={VanillaButtonStyle} onClick={goToMaps}>
                          <Stack
                            sx={{
                              flexDirection: 'row',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <PlaceOutlined
                              fontSize="small"
                              sx={{
                                color: theme.palette.text.primary,
                              }}
                            />
                            <TextBodyBold
                              sx={{
                                color: theme.palette.text.brand,
                                textDecoration: 'underline',
                                textAlign: 'start',
                              }}
                            >
                              {institutionData?.address}
                            </TextBodyBold>
                          </Stack>
                        </button>
                      )}
                      <TextBody>{institutionData?.presentation}</TextBody>
                    </Stack>
                  </>
                )}
              </Stack>

              {/**@RightSection - CTA */}
              <Stack sx={{ display: { xs: 'none', md: 'block' } }}>
                {offerData && institutionData && (
                  <ApplyCard
                    name={institutionData?.name as string}
                    address={institutionData?.address ?? undefined}
                    apply={goToJobApply}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </>
  )
}
